import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, Loader2, Plane, MapPin, Building2, Globe, Plane as PlaneIcon, Clock, ExternalLink, Users } from "lucide-react";
import api from "../../lib/api";
import AirlineLogo from "./AirlineLogo";

const TYPE_META = {
  airline: { label: "Airline", icon: Building2 },
  aircraft: { label: "Aircraft", icon: Plane },
  airport: { label: "Airport", icon: PlaneIcon },
  country: { label: "Country", icon: Globe },
  city: { label: "City", icon: MapPin },
  flight: { label: "Flight", icon: Plane },
};

const Row = ({ k, v, onClick, testid }) => (v || v === 0) ? (
  <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
    <dt className="text-[#636E72]">{k}</dt>
    {onClick ? (
      <dd><button data-testid={testid} onClick={onClick} className="font-medium text-[#0369A1] underline decoration-dotted underline-offset-2 hover:text-[#0F766E]">{v}</button></dd>
    ) : <dd className="text-right font-medium text-[#2D3436]">{v}</dd>}
  </div>
) : null;

const SectionTitle = ({ children }) => <p className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">{children}</p>;

const StatGrid = ({ items }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="panel-stats">
    {items.filter((i) => i).map((i) => (
      <div key={i.label} className="rounded-xl border border-[#EBECE8] bg-[#FAFAF8] px-3 py-2.5">
        <div className="text-lg font-semibold text-[#2D3436]">{i.value}</div>
        <div className="text-[11px] text-[#636E72]">{i.label}</div>
      </div>
    ))}
  </div>
);

const Placeholders = ({ items }) => (
  <div className="mt-2 flex flex-wrap gap-1.5">
    {items.map((p) => (
      <span key={p} className="inline-flex items-center gap-1 rounded-full border border-[#EBECE8] bg-[#FAFAF8] px-2.5 py-1 text-[11px] text-[#9aa3a0]">
        {p}<span className="rounded-full bg-[#EBECE8] px-1.5 text-[9px] font-semibold uppercase text-[#636E72]">soon</span>
      </span>
    ))}
  </div>
);

function localTime(tz) {
  if (!tz) return null;
  try { return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: tz }).format(new Date()); } catch { return null; }
}
const fT = (s) => (s && s.length >= 16 ? s.slice(11, 16) : "-");

// ---- per-type bodies ----------------------------------------------------------------------------
function AirlineBody({ k, payload, openObject }) {
  const [d, setD] = useState(null); const [err, setErr] = useState("");
  useEffect(() => { api.get(`/travel/objects/airline/${k}`).then((r) => setD(r.data)).catch(() => setErr("Airline details unavailable")); }, [k]);
  if (err) return <p className="text-sm text-[#9aa3a0]">{err}</p>;
  if (!d) return <PanelLoading hint={payload} />;
  const a = d.airline, s = d.stats || {};
  return (
    <>
      <div className="flex items-center gap-3">
        <AirlineLogo logoUrl={a.logo_url} name={a.name} size={52} />
        <div><h3 className="font-display text-lg font-semibold text-[#2D3436]">{a.name}</h3><div className="text-xs text-[#636E72]">{a.iata}{a.icao ? ` · ${a.icao}` : ""}{a.callsign ? ` · ${a.callsign}` : ""}</div></div>
      </div>
      <dl className="mt-3 divide-y divide-[#F4F5F2]">
        <Row k="Country" v={a.country?.name} onClick={a.country ? () => openObject("country", a.country.code) : null} testid="panel-airline-country" />
        <Row k="Fleet size" v={a.fleet_size} />
        <Row k="Average fleet age" v={a.fleet_avg_age ? `${a.fleet_avg_age} yrs` : null} />
        {a.website && <Row k="Website" v={<span className="inline-flex items-center gap-1">Visit <ExternalLink className="h-3 w-3" /></span>} onClick={() => window.open(a.website.startsWith("http") ? a.website : `https://${a.website}`, "_blank")} testid="panel-airline-website" />}
      </dl>
      <SectionTitle>Your history with {a.name}</SectionTitle>
      <StatGrid items={[
        { label: "Flights", value: s.flights ?? 0 },
        { label: "Trips", value: s.trips ?? 0 },
        { label: "Routes flown", value: s.routes ?? 0 },
        { label: "Aircraft flown", value: s.aircraft_flown ?? 0 },
      ]} />
      {s.favourite_route && <p className="mt-2 text-xs text-[#636E72]">Favourite route: <span className="font-medium text-[#2D3436]">{s.favourite_route}</span></p>}
      <SectionTitle>Coming soon</SectionTitle>
      <Placeholders items={["Loyalty programme", "Route map", "On-time performance"]} />
    </>
  );
}

function AircraftBody({ k, payload, openObject }) {
  const [d, setD] = useState(null); const [err, setErr] = useState("");
  useEffect(() => { api.get(`/travel/objects/aircraft/${encodeURIComponent(k)}`).then((r) => setD(r.data)).catch(() => setErr("Aircraft details unavailable")); }, [k]);
  if (err) return <p className="text-sm text-[#9aa3a0]">{err}</p>;
  if (!d) return <PanelLoading hint={payload} />;
  const a = d.aircraft, s = d.stats || {};
  return (
    <>
      <h3 className="font-display text-lg font-semibold text-[#2D3436]">{a.model || a.icao_type || "Aircraft"}</h3>
      <div className="text-xs text-[#636E72]">{a.reg_number}{a.icao_type ? ` · ${a.icao_type}` : ""}</div>
      <dl className="mt-3 divide-y divide-[#F4F5F2]">
        <Row k="Manufacturer" v={a.manufacturer} />
        <Row k="Registration" v={a.reg_number} />
        <Row k="Engines" v={a.engine_count ? `${a.engine_count} × ${a.engine || "jet"}` : a.engine} />
        <Row k="Built" v={a.built} />
        <Row k="Operator" v={a.airline?.name} onClick={a.airline ? () => openObject("airline", a.airline.iata) : null} testid="panel-aircraft-airline" />
      </dl>
      <SectionTitle>Your history</SectionTitle>
      <StatGrid items={[
        { label: "Flights", value: s.flights ?? 0 },
        { label: "Trips", value: s.trips ?? 0 },
        { label: "Routes", value: s.routes ?? 0 },
      ]} />
      <SectionTitle>Coming soon</SectionTitle>
      <Placeholders items={["Seat map", "Cabin layout", "Range", "Cruising speed", "Photos"]} />
    </>
  );
}

function CountryBody({ k, payload, openObject }) {
  const [d, setD] = useState(null); const [err, setErr] = useState("");
  useEffect(() => { api.get(`/travel/objects/country/${k}`).then((r) => setD(r.data)).catch(() => setErr("Country details unavailable")); }, [k]);
  if (err) return <p className="text-sm text-[#9aa3a0]">{err}</p>;
  if (!d) return <PanelLoading hint={payload} />;
  const c = d.country, s = d.stats || {};
  return (
    <>
      <h3 className="font-display text-lg font-semibold text-[#2D3436]">{c.name}</h3>
      <div className="text-xs text-[#636E72]">{c.code}{c.code3 ? ` · ${c.code3}` : ""}</div>
      <dl className="mt-3 divide-y divide-[#F4F5F2]">
        <Row k="Continent" v={c.continent} />
        <Row k="Currency" v={c.currency} />
      </dl>
      <SectionTitle>Your travel here</SectionTitle>
      <StatGrid items={[
        { label: "Flights", value: s.flights ?? 0 },
        { label: "Trips", value: s.trips ?? 0 },
        { label: "Airports used", value: (s.airports_used || []).length },
      ]} />
      {(s.airports_used || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {s.airports_used.map((ap) => (
            <button key={ap} data-testid={`panel-country-airport-${ap}`} onClick={() => openObject("airport", ap)}
              className="inline-flex items-center gap-1 rounded-full border border-[#EBECE8] bg-white px-2.5 py-1 text-xs font-medium text-[#0369A1] hover:bg-[#0EA5E9]/5">
              <PlaneIcon className="h-3 w-3" /> {ap}
            </button>
          ))}
        </div>
      )}
      <SectionTitle>Coming soon</SectionTitle>
      <Placeholders items={["Destination guide", "Government travel advice", "Visa requirements", "Cities visited"]} />
    </>
  );
}

function CityBody({ k, payload, openObject }) {
  const [d, setD] = useState(null); const [err, setErr] = useState("");
  useEffect(() => { api.get(`/travel/objects/city/${k}`).then((r) => setD(r.data)).catch(() => setErr("City details unavailable")); }, [k]);
  if (err) return <p className="text-sm text-[#9aa3a0]">{err}</p>;
  if (!d) return <PanelLoading hint={payload} />;
  const c = d.city;
  return (
    <>
      <h3 className="font-display text-lg font-semibold text-[#2D3436]">{c.name}</h3>
      <dl className="mt-3 divide-y divide-[#F4F5F2]">
        <Row k="Country" v={c.country?.name} onClick={c.country ? () => openObject("country", c.country.code) : null} testid="panel-city-country" />
        <Row k="Local time" v={localTime(c.timezone) ? `${localTime(c.timezone)} now` : null} />
        <Row k="Timezone" v={c.timezone} />
      </dl>
      <SectionTitle>Coming soon</SectionTitle>
      <Placeholders items={["Destination guide", "Things to do", "Best time to visit", "Weather"]} />
    </>
  );
}

function AirportBody({ k, payload, openObject }) {
  const [d, setD] = useState(null); const [board, setBoard] = useState(null); const [nearby, setNearby] = useState(null); const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setBoard(null); setNearby(null); setErr("");
    api.get(`/travel/objects/airport/${k}`).then((r) => setD(r.data)).catch(() => setErr("Airport details unavailable"));
    api.get(`/travel/airports/${k}/schedules`, { params: { direction: "dep" } }).then((r) => setBoard(r.data.board || [])).catch(() => setBoard([]));
    api.get(`/travel/airports/${k}/nearby`).then((r) => setNearby(r.data.airports || [])).catch(() => setNearby([]));
  }, [k]);
  if (err) return <p className="text-sm text-[#9aa3a0]">{err}</p>;
  if (!d) return <PanelLoading hint={payload} />;
  const a = d.airport, s = d.stats || {};
  const mapUrl = a.lat != null && a.lng != null ? `https://www.google.com/maps/search/?api=1&query=${a.lat},${a.lng}` : null;
  return (
    <>
      <h3 className="font-display text-lg font-semibold text-[#2D3436]">{a.name}</h3>
      <div className="text-xs text-[#636E72]">{a.iata}{a.icao ? ` · ${a.icao}` : ""}</div>
      <dl className="mt-3 divide-y divide-[#F4F5F2]">
        <Row k="City" v={a.city_name} onClick={a.city_id ? () => openObject("city", a.city_id) : null} testid="panel-airport-city" />
        <Row k="Country" v={a.country?.name} onClick={a.country ? () => openObject("country", a.country.code) : null} testid="panel-airport-country" />
        <Row k="Local time" v={localTime(a.timezone) ? `${localTime(a.timezone)} now` : null} />
        <Row k="Elevation" v={a.elevation ? `${a.elevation} ft` : null} />
      </dl>
      {mapUrl && <a data-testid="panel-airport-map" href={mapUrl} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-1.5 rounded-xl border border-[#EBECE8] px-3 py-2 text-sm font-medium text-[#0F766E] hover:bg-[#0F766E]/5"><MapPin className="h-4 w-4" /> View on map</a>}
      <SectionTitle>Your travel here</SectionTitle>
      <StatGrid items={[{ label: "Flights", value: s.flights ?? 0 }, { label: "Trips", value: s.trips ?? 0 }]} />
      <SectionTitle>Live departures</SectionTitle>
      {board === null ? <div className="flex items-center gap-2 py-3 text-xs text-[#9aa3a0]"><Loader2 className="h-4 w-4 animate-spin" /> Loading board…</div>
        : board.length === 0 ? <p className="rounded-lg border border-[#EBECE8] bg-[#FAFAF8] px-3 py-2.5 text-xs text-[#9aa3a0]">No live schedule information available.</p>
        : (
          <div className="divide-y divide-[#F0F1EE] overflow-hidden rounded-xl border border-[#EBECE8]">
            {board.slice(0, 8).map((b, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-11 shrink-0 font-semibold tabular-nums text-[#2D3436]">{fT(b.time)}</span>
                <span className="min-w-0 flex-1 truncate">To {b.counterpart_iata} · {b.airline_name} {b.flight_iata}</span>
              </div>
            ))}
          </div>
        )}
      {nearby && nearby.length > 0 && (<>
        <SectionTitle>Nearby airports</SectionTitle>
        <div className="flex flex-wrap gap-1.5">
          {nearby.map((n) => (
            <button key={n.iata} data-testid={`panel-airport-nearby-${n.iata}`} onClick={() => openObject("airport", n.iata)}
              className="inline-flex items-center gap-1 rounded-full border border-[#EBECE8] bg-white px-2.5 py-1 text-xs font-medium text-[#0369A1] hover:bg-[#0EA5E9]/5" title={n.name}>
              <PlaneIcon className="h-3 w-3" /> {n.iata}{n.distance != null ? <span className="text-[#9aa3a0]">{Math.round(n.distance)}km</span> : null}
            </button>
          ))}
        </div>
      </>)}
      <SectionTitle>Airport services</SectionTitle>
      <Placeholders items={["Lounges", "Parking", "Transport", "Restaurants", "Retail", "Security wait"]} />
    </>
  );
}

const STATUS_TONE = {
  scheduled: { bg: "#EEF2FF", fg: "#4338CA" }, active: { bg: "#ECFDF5", fg: "#047857" },
  landed: { bg: "#F0FDF4", fg: "#15803D" }, cancelled: { bg: "#FEF2F2", fg: "#B91C1C" },
  delayed: { bg: "#FFFBEB", fg: "#B45309" }, diverted: { bg: "#FFF7ED", fg: "#C2410C" },
};

function FlightBody({ k, payload, openObject }) {
  const [d, setD] = useState(payload && payload.flight_iata ? { flight: payload, trip: null } : null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/travel/objects/flight/${encodeURIComponent(k)}`).then((r) => setD(r.data)).catch(() => { if (!payload) setErr("Flight details unavailable"); });
  }, [k]);
  if (err) return <p className="text-sm text-[#9aa3a0]">{err}</p>;
  if (!d) return <PanelLoading hint={payload} />;
  const f = d.flight || {};
  const st = STATUS_TONE[(f.status || "").toLowerCase()] || { bg: "#F3F4F6", fg: "#636E72" };
  // Distinct aircraft states: Confirmed (exact registration) > Planned (type only) > Not yet
  // available (future/scheduled) > Not recorded (already flown but tracking was never on).
  const flown = ["landed", "arrived", "active", "en-route"].includes((f.status || "").toLowerCase());
  const plannedType = f.model || f.aircraft_icao;
  let aircraftRow, aircraftNote = "";
  if (f.reg_number) {
    aircraftRow = (
      <div className="flex items-center justify-between gap-3 py-2 text-sm">
        <dt className="text-[#636E72]">Aircraft</dt>
        <dd className="flex items-center gap-2">
          <span className="rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-semibold text-[#047857]">Confirmed</span>
          <button data-testid="panel-flight-aircraft" onClick={() => openObject("aircraft", f.reg_number, { name: f.model || f.reg_number })} className="font-medium text-[#0369A1] hover:underline">{f.model || f.aircraft_icao || f.reg_number}</button>
        </dd>
      </div>
    );
  } else if (plannedType) {
    aircraftRow = (
      <div className="flex items-center justify-between gap-3 py-2 text-sm" data-testid="panel-flight-aircraft">
        <dt className="text-[#636E72]">Aircraft</dt>
        <dd className="flex items-center gap-2">
          <span className="rounded-full bg-[#FFFBEB] px-2 py-0.5 text-[10px] font-semibold text-[#B45309]">Planned</span>
          <span className="font-medium text-[#2D3436]">{f.model || f.aircraft_icao}</span>
        </dd>
      </div>
    );
    aircraftNote = f.aircraft_source === "schedule"
      ? "Planned aircraft type from the airline's published schedule for this flight. The exact aircraft is captured live on travel day when Track this flight is on."
      : "Planned aircraft type based on this route's usual aircraft. The exact aircraft is captured live on travel day when Track this flight is on.";
  } else {
    aircraftRow = <Row k="Aircraft" v={flown ? "Not recorded" : "Not yet available"} testid="panel-flight-aircraft" />;
    aircraftNote = flown
      ? "The exact aircraft was not recorded because live tracking was not enabled while the flight was airborne."
      : "Not yet available. Turn on Track this flight and PlannioLife will record the exact aircraft on travel day.";
  }
  return (
    <>
      <div className="flex items-center gap-3">
        <AirlineLogo logoUrl={f.airline_logo} name={f.airline_name} size={48} />
        <div className="min-w-0">
          <h3 className="font-display text-lg font-semibold text-[#2D3436]">{f.flight_iata}</h3>
          <button data-testid="panel-flight-airline" onClick={() => f.airline_iata && openObject("airline", f.airline_iata, { name: f.airline_name, logo_url: f.airline_logo })} className="truncate text-xs text-[#0369A1] hover:underline">{f.airline_name || f.airline_iata}</button>
        </div>
        {f.status && <span className="ml-auto rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize" style={{ background: st.bg, color: st.fg }}>{f.status}</span>}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-2xl border border-[#EBECE8] bg-[#FAFAF8] p-4">
        <button data-testid="panel-flight-dep" onClick={() => f.dep_iata && openObject("airport", f.dep_iata)} className="text-center">
          <div className="font-display text-2xl font-semibold text-[#0369A1]">{f.dep_iata || "-"}</div>
          <div className="text-[11px] text-[#636E72]">{fT(f.dep_time)}{f.dep_terminal ? ` · T${f.dep_terminal}` : ""}</div>
        </button>
        <Plane className="h-5 w-5 rotate-90 text-[#9aa3a0]" />
        <button data-testid="panel-flight-arr" onClick={() => f.arr_iata && openObject("airport", f.arr_iata)} className="text-center">
          <div className="font-display text-2xl font-semibold text-[#0369A1]">{f.arr_iata || "-"}</div>
          <div className="text-[11px] text-[#636E72]">{fT(f.arr_time)}{f.arr_terminal ? ` · T${f.arr_terminal}` : ""}</div>
        </button>
      </div>
      <dl className="mt-3 divide-y divide-[#F4F5F2]">
        {aircraftRow}
        <Row k="Registration" v={f.reg_number || (plannedType ? "Not yet recorded" : null)} />
        <Row k="Gate" v={f.dep_gate} />
        <Row k="Trip" v={d.trip?.trip_name} onClick={d.trip ? () => { window.location.href = `/app/travel/${d.trip.id}`; } : null} testid="panel-flight-trip" />
      </dl>
      {aircraftNote && <p data-testid="panel-flight-aircraft-note" className="mt-3 rounded-lg border border-[#EBECE8] bg-[#FAFAF8] px-3 py-2.5 text-xs text-[#9aa3a0]">{aircraftNote}</p>}
      <SectionTitle>Coming soon</SectionTitle>
      <Placeholders items={["Live map", "Seat information", "Baggage belt"]} />
    </>
  );
}

const PanelLoading = ({ hint }) => (
  <>
    {hint?.name && (
      <div className="mb-3 flex items-center gap-3">
        {hint.logo_url !== undefined && <AirlineLogo logoUrl={hint.logo_url} name={hint.name} size={44} />}
        <h3 className="font-display text-lg font-semibold text-[#2D3436]">{hint.name}</h3>
      </div>
    )}
    <div className="flex items-center gap-2 py-6 text-sm text-[#9aa3a0]"><Loader2 className="h-4 w-4 animate-spin" /> Loading details…</div>
  </>
);

const BODIES = { airline: AirlineBody, aircraft: AircraftBody, country: CountryBody, city: CityBody, airport: AirportBody, flight: FlightBody };

export default function ObjectPanelHost({ item, canBack, onBack, onClose, openObject }) {
  const meta = TYPE_META[item.type] || TYPE_META.airport;
  const Body = BODIES[item.type];
  const Icon = meta.icon;
  return createPortal(
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid="object-panel" role="dialog" aria-label={`${meta.label} details`}>
      <div className="absolute inset-0 bg-[#2D3436]/45 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-2xl sm:animate-in sm:slide-in-from-right">
        <div className="flex items-center gap-2 border-b border-[#F0F1EE] px-4 py-3">
          {canBack && <button data-testid="object-panel-back" onClick={onBack} className="text-[#9aa3a0] hover:text-[#2D3436]"><ChevronLeft className="h-5 w-5" /></button>}
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]"><Icon className="h-3.5 w-3.5" /> {meta.label}</span>
          <button data-testid="object-panel-close" onClick={onClose} className="ml-auto text-[#9aa3a0] hover:text-[#2D3436]"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {Body ? <Body k={item.key} payload={item.payload} openObject={openObject} /> : <p className="text-sm text-[#9aa3a0]">Unsupported object.</p>}
        </div>
      </div>
    </div>,
    document.body
  );
}
